import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import AppraisalsService from "app/api/AppraisalsService";

const MAX_EXPORT_MONTHS = 3;

const PASSWORD_WORDS = [
  "Tiger", "Ocean", "River", "Storm", "Amber", "Cedar", "Eagle", "Maple",
  "Coral", "Blaze", "Frost", "Raven", "Stone", "Pearl", "Cloud", "Flame",
  "Ember", "Birch", "Crane", "Dusk", "Flint", "Grove", "Haven", "Ivory",
  "Jade", "Kelp", "Lark", "Marsh", "Noble", "Opal", "Pine", "Quest",
  "Ridge", "Sage", "Thorn", "Unity", "Vale", "Waves", "Xenon", "Yew",
  "Zinc", "Arrow", "Brook", "Cliff", "Dawn", "Field", "Glade", "Haze",
  "Isle", "Knoll",
];

function generatePassword() {
  const word = PASSWORD_WORDS[Math.floor(Math.random() * PASSWORD_WORDS.length)];
  const digits = String(Math.floor(1000 + Math.random() * 9000));
  return word + digits;
}

const ExportModal = ({ open, onClose }) => {
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "months").startOf("month"),
  );
  const [endDate, setEndDate] = useState(moment());
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState("Copy password");
  const [hasUserSetEndDate, setHasUserSetEndDate] = useState(false);

  // Fetch the office's current-month password from BE each time modal opens
  useEffect(() => {
    if (open) {
      setPassword("");
      setError("");
      setHasUserSetEndDate(false);
      setLoadingPassword(true);
      AppraisalsService.getExportPassword()
        .then(res => setPassword(res.data.password))
        .catch(() => setError("Failed to load export password. Please close and try again."))
        .finally(() => setLoadingPassword(false));
    }
  }, [open]);

  const handleCopyPassword = useCallback(() => {
    navigator.clipboard.writeText(password).then(() => {
      setCopyTooltip("Copied!");
      setTimeout(() => setCopyTooltip("Copy password"), 2000);
    });
  }, [password]);

  const validate = (start, end) => {
    if (!start || !end) return "Please select both start and end dates.";
    if (end.isBefore(start)) return "End date must be after start date.";
    if (end.diff(start, "months", true) > MAX_EXPORT_MONTHS)
      return `Date range cannot exceed ${MAX_EXPORT_MONTHS} months.`;
    return "";
  };

  const handleStartChange = (date) => {
    setStartDate(date);
    if (!hasUserSetEndDate) {
      const autoEnd = moment.min(date.clone().add(MAX_EXPORT_MONTHS, "months").subtract(1, "day"), moment());
      setEndDate(autoEnd);
      setError(validate(date, autoEnd));
    } else {
      setError(validate(date, endDate));
    }
  };

  const handleEndChange = (date) => {
    setHasUserSetEndDate(true);
    setEndDate(date);
    setError(validate(startDate, date));
  };

  const handleExport = async () => {
    const validationError = validate(startDate, endDate);
    if (validationError) {
      setError(validationError);
      return;
    }

    setExporting(true);
    setError("");

    try {
      const start = startDate.format("YYYY-MM-DD");
      const end = endDate.format("YYYY-MM-DD");
      const res = await AppraisalsService.exportAppraisalMeta(start, end, password);

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `digikit-appraisals-${start}-to-${end}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      if (err.response?.status === 429) {
        setError(
          "Export is currently busy. Please try again in a moment.",
        );
      } else {
        setError("Export failed. Please try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      setError("");
      onClose();
    }
  };

  const validationError = validate(startDate, endDate);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Export Appraisals to Excel</DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{ marginBottom: 16, color: "#666" }}>
          Select a date range (max {MAX_EXPORT_MONTHS} months) to export sent
          appraisal metadata for your office.
        </Typography>
        <div style={{ display: "flex", gap: 16, flexDirection: "column" }}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={handleStartChange}
            format="DD/MM/YYYY"
            inputVariant="outlined"
            fullWidth
            disableFuture
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={handleEndChange}
            format="DD/MM/YYYY"
            inputVariant="outlined"
            fullWidth
            disableFuture
            minDate={startDate}
          />
          <div>
            <TextField
              label="File Password"
              value={password}
              inputProps={{ readOnly: true }}
              variant="outlined"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copyTooltip}>
                      <IconButton onClick={handleCopyPassword} edge="end" size="small">
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" style={{ color: "#e65100", marginTop: 4, display: "block" }}>
              Save this password — you will need it to open the file.
            </Typography>
          </div>
        </div>
        {(error || validationError) && (
          <Typography
            variant="body2"
            style={{ color: "#d32f2f", marginTop: 12 }}
          >
            {error || validationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "8px 24px 16px" }}>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          disabled={exporting || loadingPassword || !password || !!validationError}
          startIcon={exporting ? <CircularProgress size={16} /> : null}
        >
          {exporting ? "Generating..." : "Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportModal;
